import { React, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  Button,
  ButtonGroup,
  Heading,
  chakra,
  VStack,
  HStack,
  PinInput,
  PinInputField,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  useDisclosure,
  Image,
  Stack,
  Text,
  Flex,
  CircularProgress,
  Box,
  Skeleton,
  Center,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import NoUserHeader from '../../AreaLivre/NoUserHeader';
import { useContext } from 'react';
import { AccountContext } from '../../AccountContext';
import { enviarCodigoConfirmacao as enviarSMS } from 'api/sms';
import { enviarCodigoConfirmacao as enviarWhatsApp } from 'api/whatsapp';
import { ReactComponent as SadCircle } from 'images/sad-circle.svg';
import { updatePhone } from 'api/client';
import { api } from 'api/api';

function BotaoEnviarWhatsApp({ sendWhatsApp, successWhatsApp, successSMS }) {
  const interval = useRef();
  const countdown = 10; // 10 segundos
  const [counter, setCounter] = useState(countdown);

  useEffect(() => {
    if (counter > 0) {
      interval.current = setInterval(() => {
        setCounter((prevCounter) => prevCounter - 1);
      }, 1000);
      return () => clearInterval(interval.current);
    }
  }, [counter]);

  return (
    <>
      <Button
        mt={4}
        isFullWidth
        disabled={counter}
        onClick={() => {
          sendWhatsApp();
          setCounter(60);
        }}
      >
        {successWhatsApp ? 'Enviar novo código' : 'Enviar código via WhatsApp'}
      </Button>

      {successWhatsApp && !!counter ? (
        <Flex alignItems={'center'} justifyContent={'space-between'} mt={4} gap={4}>
          <Text fontSize={10}>Aguarde {`${counter} segundos`}, para enviar um novo código</Text>
          <CircularProgress value={((countdown - counter) / countdown) * 100} size={4} thickness={20} color="teal" />
        </Flex>
      ) : (
        successSMS &&
        !!counter && (
          <Flex alignItems={'center'} justifyContent={'space-between'} mt={4} gap={4}>
            <Text fontSize={10}>
              Caso o SMS não seja entregue em até {counter > 60 ? `${Math.round(counter / 60)} min` : `${counter} segundos`}, você poderá enviar a
              mensagem via WhatsApp
            </Text>
            <CircularProgress value={((countdown - counter) / countdown) * 100} size={4} thickness={20} color="teal" />
          </Flex>
        )
      )}
    </>
  );
}

const ENDPOINT =
  process.env.REACT_APP_ENVIRONMENT === 'production'
    ? process.env.REACT_APP_BASE_URL_API_SERVER_PROD
    : process.env.REACT_APP_BASE_URL_API_SERVER_TEST;

const formatCpf = (str) => str.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
const onlyNumbers = (str) => str.replace(/[^0-9]/g, '');

function GeraCodigo() {
  const { state } = useLocation();
  console.log(state)
  const { setPage } = useContext(AccountContext);

  const [successSMS, setSuccessSMS] = useState(false);
  const [successWhatsApp, setSuccessWhatsApp] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const firstName = state.name.split(' ').at(0);
  const mustUpdatePhone = state.mustUpdatePhone ? true : false;

  const sendSMS = useCallback(async () => {
    setError(null);
    setLoading(true);
    let data = null;
    try {
      data = await enviarSMS(state.cpf, onlyNumbers(state.phone));
      setSuccessSMS(!!data.success);
    } catch (error) {
      console.error(error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
    return data;
  }, [state.cpf, state.phone]);

  const sendWhatsApp = useCallback(async () => {
    setError(null);
    setLoading(true);
    let data = null;
    try {
      data = await enviarWhatsApp(state.cpf, state.phone, state.name);
      setSuccessWhatsApp(data?.response?.status === 'success');
    } catch (error) {
      console.error(error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
    return data;
  }, [state.cpf, state.name, state.phone]);

  const sendCode = useCallback(async () => {
    const resultSMS = await sendSMS();

    if (!resultSMS?.success) {
      const resultWhatsApp = await sendWhatsApp();
      if (resultWhatsApp?.response?.status !== 'success') {
        setError('Erro ao enviar código de confirmação. Por favor, tente novamente mais tarde, ou contate o suporte.');
      }
    }
  }, [sendSMS, sendWhatsApp]);

  const schema = Yup.object({
    pin: Yup.string().required('Digite o código de confirmação').length(8, 'O código deve ter exatamente 8 dígitos'),
  });

  useEffect(() => {
    sendCode();
  }, [sendCode]);

  useEffect(() => {
    setPage('Confirmação de Telefone');
  }, [setPage]);

  if (loading) {
    return (
      <>
        <NoUserHeader />
        <Box p={8}>
          <Flex flexDirection={'column'} gap={8}>
            <Skeleton rounded={'lg'}>
              <Heading fontSize="1em">
                {`Olá ${firstName}, enviamos ${
                  successWhatsApp ? 'uma mensagem no WhatsApp' : successSMS ? 'um SMS' : null
                } com seu código de segurança.`}
                <Text fontSize={'xs'} mt={4}>
                  Insira-o abaixo e em seguida clique "Confirmar"
                </Text>
              </Heading>
            </Skeleton>

            <HStack spacing={2}>
              <PinInput as={Skeleton} isDisabled size="md">
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
              </PinInput>
            </HStack>
            <Button as={Skeleton} isFullWidth disabled isLoading>
              Confirmar
            </Button>
          </Flex>
          <Button as={Skeleton} mt={4} isFullWidth disabled isLoading>
            {successWhatsApp ? 'Enviar novo código' : 'Enviar código via WhatsApp'}
          </Button>

          <Flex alignItems={'center'} justifyContent={'space-between'} mt={4} gap={4}>
            <Text as={Skeleton} fontSize={10} rounded={'lg'}>
              Caso o SMS não seja entregue em até 60 segundos, você poderá enviar a mensagem via WhatsApp
            </Text>
            <CircularProgress value={0} size={4} thickness={20} />
          </Flex>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <NoUserHeader />
        <Box p={8}>
          <Center flexDirection={'column'} gap={8}>
            <SadCircle />
            <Text fontSize="xl" textAlign={'center'} fontWeight="bold">
              Não foi possível enviar o código de confirmação.
            </Text>
            <Text textAlign={'center'}>Por favor, tente novamente mais tarde, ou contate o suporte.</Text>

            <Button
              onClick={() => {
                sendCode();
              }}
            >
              Tentar novamente
            </Button>
          </Center>
        </Box>
      </>
    );
  }

  return (
    <>
      {/* <Modal isOpen={sair} isCentered>
        <ModalOverlay />
        <ModalContent height="300px" width="320px" border-radius="16px">
          <ModalHeader></ModalHeader>
          <ModalBody>
            <CheckCircleIcon ml="100" color="green" boxSize={20} />
            <Text size="lg" ml="3" mt="15" textAlign="justify">
              Um novo código foi enviado para o número cadastrado
            </Text>
          </ModalBody>
          <VStack ml="4">
            <ModalFooter>
              <Stack direction="row" ml="-4" mt="-5" spacing={5} align="center">
                <Button
                  colorScheme="teal"
                  variant="solid"
                  size="lg"
                  onClick={() => {
                    setSair(false);
                  }}
                >
                  Ok
                </Button>
              </Stack>
            </ModalFooter>
          </VStack>
        </ModalContent>
      </Modal> */}

      <NoUserHeader />
      <Box p={8}>
        <Formik
          validateOnBlur
          initialValues={{ pin: '' }}
          validationSchema={schema}
          onSubmit={async (values, actions) => {
            try {
              const result = await api(`${ENDPOINT}/auth/recovery`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  codigo: values.pin,
                  cpf: state.cpf,
                }),
              });

              const data = await result.json();

              if (data?.success === true) {
                setError(null);
                if (mustUpdatePhone) {
                  const resultUpdate = await updatePhone(state.phone, state.paciente_id);
                  if (resultUpdate?.success === true) {
                    navigate('/recuperacao/senha', { state: { cpf: state.cpf, nome: firstName, hash: data.hash } });
                  }
                } else {
                  navigate('/recuperacao/senha', { state: { cpf: state.cpf, nome: firstName, hash: data.hash } });
                }
              } else {
                actions.setFieldError('pin', 'Código incorreto');
              }
            } catch (error) {
              actions.setFieldError('pin', 'Não foi possível verificar o código digitado. Tente novamente mais tarde.');
            } finally {
              actions.setSubmitting(false);
            }
          }}
        >
          {(props) => {
            return (
              <Flex as={Form} flexDirection={'column'} gap={8}>
                <Heading fontSize="1em">
                  {`Olá ${firstName}, enviamos ${
                    successWhatsApp ? 'uma mensagem no WhatsApp' : successSMS ? 'um SMS' : null
                  } com seu código de segurança.`}
                  <Text fontSize={'xs'} mt={4}>
                    Insira-o abaixo e em seguida clique "Confirmar"
                  </Text>
                </Heading>

                <Field name="pin">
                  {({ field, form }) => {
                    return (
                      <FormControl isInvalid={form.errors.pin && form.touched.pin}>
                        <HStack spacing={2}>
                          <PinInput
                            size="md"
                            id="pin"
                            value={field.value}
                            onChange={(value) => form.setFieldValue('pin', value)}
                            onBlur={form.handleBlur}
                            otp
                            inputProps={{ textContentType: 'oneTimeCode', keyboardType: 'numeric', autoComplete: 'one-time-code' }}
                            isInvalid={form.errors.pin && form.touched.pin}
                          >
                            <PinInputField />
                            <PinInputField />
                            <PinInputField />
                            <PinInputField />
                            <PinInputField />
                            <PinInputField />
                            <PinInputField />
                            <PinInputField />
                          </PinInput>
                        </HStack>
                        <FormErrorMessage mt={4}>{form.errors.pin}</FormErrorMessage>
                      </FormControl>
                    );
                  }}
                </Field>

                <Button isFullWidth colorScheme="teal" type="submit" disabled={!props.dirty || !props.isValid} isLoading={props.isSubmitting}>
                  Confirmar
                </Button>
              </Flex>
            );
          }}
        </Formik>

        <BotaoEnviarWhatsApp sendWhatsApp={sendWhatsApp} successWhatsApp={successWhatsApp} successSMS={successSMS} />

        {!mustUpdatePhone && (
          <Button
            variant="link"
            colorScheme="teal"
            size="xs"
            mt={4}
            onClick={() =>
              navigate('/recuperacao/security-questions', { state: { name: state.name, paciente_id: state.paciente_id, cpf: state.cpf } })
            }
          >
            Outra opção para redefinir a senha
          </Button>
        )}
      </Box>
    </>
  );
}

export default GeraCodigo;
